import { z } from "zod";

import { BRAND_MARKUP } from "../../constants";
import { ValidationError } from "../../errors";
import { attachment } from "../shared";
import { BaseOptionItem, defineModel } from "../types";

const SEEDREAM_ASPECT_RATIOS = [
  "1:1",
  "4:3",
  "3:4",
  "16:9",
  "9:16",
  "3:2",
  "2:3",
  "21:9",
] as const;

const SEEDREAM_ASPECT_RATIO_OPTIONS: readonly BaseOptionItem[] =
  SEEDREAM_ASPECT_RATIOS.map((value) => ({
    label: value,
    value,
  }));

const RESOLUTION_VALUES = ["2K", "4K"] as const;
const RESOLUTION_OPTIONS: readonly BaseOptionItem[] = RESOLUTION_VALUES.map(
  (value) => ({
    label: value,
    value,
  }),
);

const SEEDREAM_2K_DIMENSIONS = {
  "1:1": { width: 2048, height: 2048 },
  "4:3": { width: 2304, height: 1728 },
  "3:4": { width: 1728, height: 2304 },
  "16:9": { width: 2560, height: 1440 },
  "9:16": { width: 1440, height: 2560 },
  "3:2": { width: 2496, height: 1664 },
  "2:3": { width: 1664, height: 2496 },
  "21:9": { width: 3024, height: 1296 },
} as const;

const SEEDREAM_4K_DIMENSIONS = {
  "1:1": { width: 4096, height: 4096 },
  "4:3": { width: 4608, height: 3456 },
  "3:4": { width: 3456, height: 4608 },
  "16:9": { width: 5120, height: 2880 },
  "9:16": { width: 2880, height: 5120 },
  "3:2": { width: 4992, height: 3328 },
  "2:3": { width: 3328, height: 4992 },
  "21:9": { width: 6048, height: 2592 },
} as const;

type SeedreamAspectRatio = keyof typeof SEEDREAM_2K_DIMENSIONS;
type SeedreamResolution = (typeof RESOLUTION_VALUES)[number];

function getSeedreamDimension(
  aspectRatio: SeedreamAspectRatio,
  resolution: SeedreamResolution,
) {
  return resolution === "2K"
    ? SEEDREAM_2K_DIMENSIONS[aspectRatio]
    : SEEDREAM_4K_DIMENSIONS[aspectRatio];
}

const seedream45Params = z
  .object({
    aspectRatio: z.enum(SEEDREAM_ASPECT_RATIOS).default("9:16"),
    resolution: z.enum(RESOLUTION_VALUES).default("2K"),
    attachments: z.array(attachment).optional(),
    prompt: z.string().default(""),
  })
  .strip();

export const seedream45 = defineModel({
  key: "seedream-4-5",
  stepType: "image",
  spec: {
    label: "Seedream 4.5",
    description:
      "Production-ready model with improved face rendering and text quality, supports 2K and 4K outputs",
    icon: "bytedance-logo.svg",
    tags: ["image-gen", "image-edit"],
    featured: true,
    features: {
      aspectRatio: true,
      resolution: true,
      attachments: true,
      prompts: true,
      variations: true,
    } as const,
    options: {
      attachments: Array.from({ length: 14 })
        .fill(null)
        .map((_, index) => ({
          type: "image",
          allowedFormats: ["image/jpeg", "image/webp", "image/png"],
          label: `Image ${index + 1}`,
          slot: `reference_image_${index + 1}`,
          group: "reference",
          required: false,
        })),
      aspectRatio: { items: SEEDREAM_ASPECT_RATIO_OPTIONS },
      resolution: { items: RESOLUTION_OPTIONS },
    },
    params: seedream45Params,
  },
  referencePricing: {
    provider: "fal.ai",
    url: "https://fal.ai/models/fal-ai/seedream/v4.5",
    entries: [{ unit: "per_image", unitPriceMicrodollars: 40_000 }],
  },
  providers: [
    {
      provider: "runware",
      modelId: "bytedance:seedream@4.5",
      estimateCost(_params) {
        return 0.04 * 1_000_000 * (1 + BRAND_MARKUP);
      },
      toPayload(params) {
        if (!params?.prompt || params.prompt.trim().length < 2) {
          throw new ValidationError(
            "Please enter a prompt to generate an image.",
          );
        }

        const size = getSeedreamDimension(
          params.aspectRatio,
          params.resolution,
        );
        const referenceImages =
          params.attachments
            ?.filter((attachment) =>
              attachment.slot.startsWith("reference_image_"),
            )
            .map((attachment) => attachment.data)
            .filter((data): data is string => !!data) ?? [];

        return {
          model: this.modelId,
          positivePrompt: params.prompt,
          width: size.width,
          height: size.height,
          ...(referenceImages.length > 0 ? { referenceImages } : {}),
          providerSettings: {
            bytedance: {
              optimizePromptMode: "standard", // standard / fast
            },
          },
        };
      },
    },
  ],
});
